import { useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from '@cbhq/cds-web/buttons';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, Divider } from '@cbhq/cds-web/layout';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import type { TrayRefProps } from '@cbhq/cds-web/overlays';
import { Tray } from '@cbhq/cds-web/overlays';
import { TextBody, TextTitle3 } from '@cbhq/cds-web/typography';

import { useManagedByExplanation } from '../providers/ManagedByExplanationProvider';
import { useProfileInfo } from '../providers/ProfileMenuV2Provider';

const messages = defineMessages({
  title: {
    defaultMessage: 'This is a managed account',
    id: 'ManagedByExplanationTray.title',
    description: 'Heading inside the managed-by explanation tray',
  },
  body: {
    defaultMessage:
      '{organizationName} manages this account. Some security and account settings are configured by your administrator and cannot be changed here. Contact your administrator for assistance.',
    id: 'ManagedByExplanationTray.body',
    description:
      'Body copy explaining what it means for an account to be managed by an organization',
  },
  doneButton: {
    defaultMessage: 'Done',
    id: 'ManagedByExplanationTray.doneButton',
    description: 'Button that dismisses the managed-by explanation tray',
  },
});

const footerPaddingX = { base: 4, phone: 3 } as const;

export function ManagedByExplanationTray() {
  const { formatMessage } = useIntl();
  const { isPhone } = useBreakpoints();
  const { managedByOrganizationName } = useProfileInfo();
  const { isOpen, close } = useManagedByExplanation();
  const trayRef = useRef<TrayRefProps>(null);

  const handleDone = useCallback(() => trayRef.current?.close(), []);

  if (!isOpen || !managedByOrganizationName) {
    return null;
  }

  return (
    <Tray
      ref={trayRef}
      onCloseComplete={close}
      accessibilityLabel={formatMessage(messages.title)}
      pin={isPhone ? 'bottom' : 'right'}
      showHandleBar={isPhone}
      footer={
        <VStack width="100%">
          <Divider />
          <HStack
            width="100%"
            justifyContent="flex-end"
            paddingX={footerPaddingX}
            paddingTop={2}
            paddingBottom={3}
          >
            <Button onClick={handleDone}>{formatMessage(messages.doneButton)}</Button>
          </HStack>
        </VStack>
      }
    >
      <VStack gap={2} paddingBottom={3}>
        <Box
          width={40}
          height={40}
          borderRadius={400}
          background="bgPrimaryWash"
          alignItems="center"
          justifyContent="center"
        >
          <Icon name="building" color="fgPrimary" size="m" />
        </Box>
        <TextTitle3 as="p">{formatMessage(messages.title)}</TextTitle3>
        <TextBody as="p" color="fgMuted">
          {formatMessage(messages.body, { organizationName: managedByOrganizationName })}
        </TextBody>
      </VStack>
    </Tray>
  );
}
